import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Users, Boxes, AlertTriangle, CalendarClock, DoorOpen, UserX, BookX, GraduationCap } from "lucide-react";

const CAPC = { "Presque complet": "bg-amber-100 text-amber-700", "Complet": "bg-orange-100 text-orange-700", "Dépassé": "bg-red-100 text-red-700" };

const KPI = ({ icon: Icon, label, value, accent, testid }) => (
  <Card data-testid={testid} className="card-float border-slate-200 p-5 flex items-center gap-4">
    <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${accent}`}><Icon className="h-5 w-5" /></div>
    <div><div className="text-2xl font-display font-bold text-slate-900 tabular-nums">{value}</div>
      <div className="text-xs text-slate-500">{label}</div></div>
  </Card>
);

export default function SchoolDashboard() {
  const { t } = useI18n();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/school/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-400">{t("loading")}</div>;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }} data-testid="school-dashboard">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900">{t("schoolDashboard")}</h1>
          <p className="text-sm text-slate-500">{t("activeYear")} : <span className="font-medium text-slate-700">{d.active_year?.name || "—"}</span></p>
        </div>
        <Link to="/audit?module=school" data-testid="school-history-link" className="text-sm text-primary hover:underline whitespace-nowrap">{t("viewHistory")}</Link>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <KPI testid="kpi-groups" icon={Boxes} label={t("internalGroups")} value={d.groups_count} accent="bg-sky-100 text-sky-700" />
        <KPI testid="kpi-students" icon={Users} label={t("studentsAssigned")} value={d.students_assigned} accent="bg-emerald-100 text-emerald-700" />
        <KPI testid="kpi-full" icon={GraduationCap} label={t("groupsFull")} value={d.groups_full} accent="bg-amber-100 text-amber-700" />
        <KPI testid="kpi-nogroup" icon={UserX} label={t("studentsNoGroup")} value={d.students_without_group} accent="bg-rose-100 text-rose-700" />
        <KPI testid="kpi-noteacher" icon={UserX} label={t("teachersNoAssignment")} value={d.teachers_without_assignment} accent="bg-orange-100 text-orange-700" />
        <KPI testid="kpi-subnoteacher" icon={BookX} label={t("subjectsNoTeacher")} value={d.subjects_without_teacher} accent="bg-violet-100 text-violet-700" />
        <KPI testid="kpi-conflicts" icon={CalendarClock} label={t("planningConflicts")} value={d.planning_conflicts} accent="bg-red-100 text-red-700" />
        <KPI testid="kpi-rooms" icon={DoorOpen} label={t("roomsUsed")} value={d.rooms_used} accent="bg-teal-100 text-teal-700" />
      </div>
      <Card className="card-float border-slate-200 p-5 mb-6" data-testid="groups-to-watch">
        <div className="flex items-center gap-2 mb-3"><GraduationCap className="h-4 w-4 text-orange-600" />
          <h2 className="font-display font-semibold text-slate-900">{t("watchGroups")}</h2></div>
        {(!d.groups_to_watch || d.groups_to_watch.length === 0) ? <p className="text-sm text-slate-400">{t("noGroupsToWatch")}</p> :
          <div className="space-y-2">{d.groups_to_watch.map((g) => (
            <div key={g.id} data-testid={`watch-${g.group_code}`} className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-slate-900">{g.group_code}</span>
                  <Badge className={`${CAPC[g.capacity_status] || "bg-slate-100"} border-0`}>{g.capacity_status}</Badge>
                  <span className="text-xs tabular-nums text-slate-500">{g.student_count}/{g.capacity_target ?? "–"}/{g.capacity_max ?? "∞"}</span>
                </div>
                <div className="text-xs text-slate-500 truncate">{(g.level_names || []).join(" · ")}</div>
              </div>
              <Link to="/school/groups" className="text-sm text-primary hover:underline whitespace-nowrap">{t("viewGroup")}</Link>
            </div>))}</div>}
      </Card>
      <Card className="card-float border-slate-200 p-5" data-testid="school-alerts">
        <div className="flex items-center gap-2 mb-3"><AlertTriangle className="h-4 w-4 text-amber-600" />
          <h2 className="font-display font-semibold text-slate-900">{t("alerts")}</h2></div>
        {(!d.alerts || d.alerts.length === 0) ? <p className="text-sm text-slate-400">{t("noData")}</p> :
          <div className="space-y-2">{d.alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-slate-700 bg-amber-50 rounded-lg px-3 py-2">
              <Badge className="bg-amber-100 text-amber-700 border-0">!</Badge>{a}</div>))}</div>}
      </Card>
    </motion.div>
  );
}
